import { BOARD_STATUSES, type TaskBoardResult, type TaskStatus, type TaskSummary } from '@fos/shared/contracts/work';

/**
 * Stan tablicy Kanban po stronie klienta: kolumny jako listy zadań, przeniesienie karty (optymistycznie,
 * zanim serwer potwierdzi) i wyliczenie sąsiada (`afterId`) dla usługi `tasks.move`.
 */

export type BoardStatus = (typeof BOARD_STATUSES)[number];
export type BoardColumns = Record<BoardStatus, TaskSummary[]>;

export const COLUMN_PREFIX = 'kolumna:';
export const DONE_ZONE_ID = 'kolumna:done';

export function columnId(status: TaskStatus): string {
  return `${COLUMN_PREFIX}${status}`;
}

export function statusFromColumnId(id: string): TaskStatus | null {
  if (!id.startsWith(COLUMN_PREFIX)) return null;
  return id.slice(COLUMN_PREFIX.length) as TaskStatus;
}

export function isBoardStatus(status: string): status is BoardStatus {
  return (BOARD_STATUSES as readonly string[]).includes(status);
}

export function columnsFromBoard(board: TaskBoardResult | undefined): BoardColumns {
  const out = Object.fromEntries(BOARD_STATUSES.map((s) => [s, [] as TaskSummary[]])) as BoardColumns;
  for (const col of board?.columns ?? []) {
    if (isBoardStatus(col.status)) out[col.status] = [...col.items];
  }
  return out;
}

/** Kolumna, w której jest zadanie albo której id to `kolumna:<status>`. */
export function findColumn(columns: BoardColumns, id: string): BoardStatus | null {
  const fromId = statusFromColumnId(id);
  if (fromId && isBoardStatus(fromId)) return fromId;
  for (const s of BOARD_STATUSES) if (columns[s].some((t) => t.id === id)) return s;
  return null;
}

/** Przenosi zadanie do kolumny `to` na pozycję `index` (bez mutacji wejścia). */
export function moveInColumns(columns: BoardColumns, taskId: string, to: BoardStatus, index: number): BoardColumns {
  const from = findColumn(columns, taskId);
  if (!from) return columns;
  const task = columns[from].find((t) => t.id === taskId);
  if (!task) return columns;
  const next = { ...columns, [from]: columns[from].filter((t) => t.id !== taskId) } as BoardColumns;
  const target = [...next[to]];
  const at = Math.max(0, Math.min(index, target.length));
  target.splice(at, 0, { ...task, status: to });
  next[to] = target;
  return next;
}

/** Usuwa zadanie z tablicy (np. upuszczone na „Gotowe”). */
export function removeFromColumns(columns: BoardColumns, taskId: string): BoardColumns {
  const from = findColumn(columns, taskId);
  if (!from) return columns;
  return { ...columns, [from]: columns[from].filter((t) => t.id !== taskId) } as BoardColumns;
}

/** Sąsiad nad zadaniem w kolumnie (`null` = zadanie jest pierwsze). */
export function afterIdOf(columns: BoardColumns, status: BoardStatus, taskId: string): string | null {
  const list = columns[status];
  const i = list.findIndex((t) => t.id === taskId);
  if (i <= 0) return null;
  return list[i - 1]?.id ?? null;
}

/** Zastosowanie przeniesienia do danych z serwera (optymistycznie, w pamięci podręcznej zapytania). */
export function applyToBoard(board: TaskBoardResult, columns: BoardColumns, doneDelta = 0): TaskBoardResult {
  return {
    ...board,
    doneThisWeek: board.doneThisWeek + doneDelta,
    columns: board.columns.map((col) => {
      if (!isBoardStatus(col.status)) return col;
      const items = columns[col.status];
      const diff = items.length - col.items.length;
      return { ...col, items, total: Math.max(0, col.total + diff) };
    }),
  };
}

/**
 * Wstawia zadanie z powrotem do kolumny na pozycję `index` (np. „Cofnij” po szybkim „Gotowe” z karty).
 * Gdy zadanie już jest na tablicy — bez zmian.
 */
export function insertIntoColumns(
  columns: BoardColumns,
  task: TaskSummary,
  status: BoardStatus,
  index: number,
): BoardColumns {
  if (findColumn(columns, task.id)) return columns;
  const target = [...columns[status]];
  target.splice(Math.max(0, Math.min(index, target.length)), 0, { ...task, status });
  return { ...columns, [status]: target };
}

/**
 * Czy kartę można wnieść do kolumny w trakcie przeciągania (17.10.2026). Do „Zgłoszeń od klientów” wraca tylko
 * zgłoszenie z portalu (`origin = 'client_portal'`) przeniesione z „Do zrobienia” — ta sama reguła co w `tasks.move`
 * (`TASK_MESSAGES.requestOnlyFromTodo`); zwykłe zadanie omija tę kolumnę. Wyjście ze zgłoszeń = przyjęcie.
 */
export function canEnterColumn(task: Pick<TaskSummary, 'origin'>, from: TaskStatus | null, to: TaskStatus): boolean {
  if (to !== 'request') return true;
  return task.origin === 'client_portal' && (from === 'todo' || from === 'request');
}
